import React, { Fragment, useState } from 'react';
import { ArrowDownOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { useAppDispatch } from '../../hooks';
import { updateSolutionDeployment, fetchSolutionDeployment } from '../../reducers/solutionDeploymentsSlice';
import { useToken } from '../../hooks/useToken';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import type { ISolutionDeploymentErrorResponse } from '../../interfaces/ISolutionDeployment';
import { sendEvent } from '../../utilities/analytics';

interface UpdateSolutionDeploymentButtonProps {
  deploymentId: number;
  size?: 'small' | 'medium' | 'large' | 'mediumPlus';
  className?: string;
  disabled?: boolean;
}

const UpdateSolutionDeploymentButton: React.FC<UpdateSolutionDeploymentButtonProps> = ({
  deploymentId,
  size = 'large',
  className = '',
  disabled = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const token = useToken();
  const dispatch = useAppDispatch();

  const handleUpdate = async () => {
    setIsUpdating(true);
    setErrorMessage(null);

    sendEvent({ name: 'deployment-update-start', properties: { deploymentProjectId: deploymentId, workflow: 'new' } });

    try {
      await dispatch(updateSolutionDeployment({ token, deploymentId })).unwrap();

      // Refresh deployment details after successful update
      await dispatch(fetchSolutionDeployment({ token, deploymentId }));
    } catch (error: any) {
      console.error('Failed to update deployment:', error);

      // Handle error response
      const errorResponse = error as ISolutionDeploymentErrorResponse;
      let errorMsg = errorResponse.message || 'Failed to update deployment';

      // Include details if available (e.g., {error: "400: {'base': ['Local file does not exist!']}"})
      if (errorResponse.details) {
        const details = errorResponse.details;
        if (details.error) {
          errorMsg += `\n\nDetails: ${details.error}`;
        } else {
          // Format details object nicely
          const detailsStr = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
          errorMsg += `\n\nDetails:\n${detailsStr}`;
        }
      }

      setErrorMessage(errorMsg);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <Button
          onClick={handleUpdate}
          intent="primary"
          size="large"
          disabled={disabled || isUpdating}
          loading={isUpdating}
          className="z-10"
        >
          <ArrowDownOnSquareIcon className="w-4 h-4 mr-1" />
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </div>

      {/* Error Dialog */}
      <Transition show={!!errorMessage} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setErrorMessage(null);
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <XMarkIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Update Failed
                        </DialogTitle>
                        <div className="absolute right-0 top-0 pr-4 pt-4">
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={() => {
                              setErrorMessage(null);
                            }}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500 whitespace-pre-wrap break-words">{errorMessage}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <Button
                      size="large"
                      intent="primary"
                      className="flex items-center"
                      onClick={() => {
                        setErrorMessage(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default UpdateSolutionDeploymentButton;
